import React, { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Context, Hooks, Util } from "@xt/xtbase";
import { useStore } from "@/models";
import { observer } from "mobx-react-lite";
import { get_ticker24h } from "@/api/v4/market";
import { get_convertConfigList } from "@/api/v4/convert";
import LineChart from "@/components/App/LineChart";
import ChangePill from "@/components/Pages/Market/MarketsPc/ChangePill";
import { usePcSparklines } from "@/components/Pages/Market/MarketsPc/usePcSparklines";
import {
  findSpotTickerRecord,
  resolveSpotDisplaySymbolsFromConvertConfig,
  spotSymbolMatchesSubTab,
} from "@/utils/spotConvertSymbols";
import { futuresPathFromSymbol } from "@/components/Pages/AssetsTrading/utils";
import { CFD_SPOT_CONVERT_PATH } from "@/components/Pages/Convert/convertResultShared";
import {
  fmtCompact,
  formatPrice,
  parseSymbolParts,
} from "@/components/Pages/Market/MarketsPc/utils";
import { IconArrowRight, IconTrendingDown, IconTrendingUp } from "./icons";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

const FILTERS = [
  { key: "crypto", labelKey: "market.subTabCrypto", fallback: "Crypto" },
  { key: "stocks", labelKey: "market.subTabStocks", fallback: "Stocks" },
  { key: "commodities", labelKey: "market.subTabCommodities", fallback: "Commodities" },
  { key: "spot", labelKey: "home.pcSpotUsdt", fallback: "Spot-USDT" },
];

function CoinIcon({ symbol, fund }) {
  const { base } = parseSymbolParts(symbol);
  const rawLogo = fund?.currencyMap?.[base]?.logo;
  if (rawLogo) {
    return <img src={rawLogo} alt="" className={styles.coinIconMd} />;
  }
  return <span className={styles.coinIconFallbackMd}>{(base || "?").slice(0, 1).toUpperCase()}</span>;
}

function isDisplayVisible(cfg) {
  if (!cfg || cfg.state === "DELISTED") return false;
  return true;
}

/** USDT-M CFD：Crypto / Stocks / Commodities（plates 260=大宗 261=股票） */
function matchCfdCategory(item, marketConfig, category) {
  const s = String(item.s || "");
  if (!s.toLowerCase().endsWith("_cfd")) return false;
  const cfg = marketConfig?.[item.s];
  if (!isDisplayVisible(cfg)) return false;
  if ((cfg.type || "").toUpperCase() !== "CFD") return false;

  const parts = s.split("_");
  const quote = (parts[1] || "").toUpperCase();
  if (quote !== "USDT") return false;

  const plates = Array.isArray(cfg.plates) ? cfg.plates : [];
  const has260 = plates.includes(260);
  const has261 = plates.includes(261);

  if (category === "commodities") return has260;
  if (category === "stocks") return has261;
  if (category === "crypto") return !has260 && !has261;
  return false;
}

function getMarketRowHref(symbol, isSpotTab) {
  if (isSpotTab) return getUrl(CFD_SPOT_CONVERT_PATH);
  return getUrl(futuresPathFromSymbol(symbol));
}

function MarketsSection() {
  const t = useTranslation();
  const [appState] = useContext(XtContext);
  const { market, fund } = useStore();
  const [tickers, setTickers] = useState([]);
  const [spotSymbols, setSpotSymbols] = useState([]);
  const [spotLoading, setSpotLoading] = useState(false);
  const [filter, setFilter] = useState("crypto");
  const [rankFilter, setRankFilter] = useState("gainers");
  const isSpot = filter === "spot";

  useEffect(() => {
    if (!market.marketConfig || !Object.keys(market.marketConfig).length) return;
    let cancelled = false;
    get_ticker24h()
      .then((data) => {
        if (cancelled) return;
        setTickers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTickers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [market.marketConfig, market.marketConfigMd5]);

  // 与移动端首页 Spot → USDT 同源：convert/config/list + spotSubTab=usdt
  useEffect(() => {
    if (!isSpot) return undefined;
    if (!market.marketConfig || !Object.keys(market.marketConfig).length) return undefined;
    let cancelled = false;
    setSpotLoading(true);
    get_convertConfigList({ isAutoPopError: false })
      .then((res) => {
        if (cancelled) return;
        const raw = res?.result ?? res?.data ?? res;
        const list = Array.isArray(raw) ? raw : [];
        const symbols = resolveSpotDisplaySymbolsFromConvertConfig(list, market.marketConfig, {})
          .filter((sym) => spotSymbolMatchesSubTab(sym, "usdt"));
        setSpotSymbols(symbols);
      })
      .catch(() => {
        if (!cancelled) setSpotSymbols([]);
      })
      .finally(() => {
        if (!cancelled) setSpotLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSpot, market.marketConfig, market.marketConfigMd5]);

  const rows = useMemo(() => {
    const cfg = market.marketConfig || {};
    let list;
    if (isSpot) {
      if (spotLoading || !tickers.length) return [];
      list = spotSymbols.map((sym) => findSpotTickerRecord(sym, tickers));
    } else {
      list = tickers.filter((item) => matchCfdCategory(item, cfg, filter));
    }
    list.sort((a, b) => {
      const ca = Number(a.cr ?? a.r ?? 0);
      const cb = Number(b.cr ?? b.r ?? 0);
      return rankFilter === "gainers" ? cb - ca : ca - cb;
    });
    return list.slice(0, 8);
  }, [tickers, filter, rankFilter, market.marketConfig, isSpot, spotSymbols, spotLoading]);

  const marketType = isSpot ? "spot" : "usdt-m";
  const sparklineSymbols = useMemo(() => rows.map((r) => r.s), [rows]);
  const { getChartData } = usePcSparklines(sparklineSymbols, marketType, true);
  const showLoading = isSpot
    ? spotLoading || (!!spotSymbols.length && !tickers.length)
    : !tickers.length;

  const chartThemeClass = (doc) => {
    const cr = Number(doc?.cr ?? doc?.r ?? 0);
    const ret = !Number.isNaN(cr) && cr !== 0 ? (cr > 0 ? "up-color" : "down-color") : "down-color";
    if (appState?.colorReverse === "1") {
      return ret === "up-color" ? "down-color" : "up-color";
    }
    return ret;
  };

  return (
    <section className={styles.sectionTight}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitleRow}>
          <IconTrendingUp size={24} className={styles.brandIcon} />
          {t("market.pcOverviewTitle") || "Market Overview"}
        </h2>
        <a href={getUrl("/market")} className={styles.sectionLink}>
          {t("home.pcAllMarkets") || "All markets"} <IconArrowRight size={16} />
        </a>
      </div>

      <div className={classNames(styles.card, styles.marketsCard)}>
        <div className={styles.marketsToolbar}>
          <div className={styles.categoryTabs} role="tablist">
            {FILTERS.map((f) => {
              const label = t(f.labelKey);
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  className={classNames(styles.categoryTab, filter === f.key && styles.categoryTabActive)}
                  onClick={() => setFilter(f.key)}
                >
                  {label && label !== f.labelKey ? label : f.fallback}
                </button>
              );
            })}
          </div>
          <div className={styles.filterGroup}>
            <button
              type="button"
              className={classNames(styles.filterBtn, rankFilter === "gainers" && styles.filterBtnUp)}
              onClick={() => setRankFilter("gainers")}
            >
              <IconTrendingUp size={14} /> {t("home.HomeMarketRateUp") || "Top Gainers"}
            </button>
            <button
              type="button"
              className={classNames(styles.filterBtn, rankFilter === "losers" && styles.filterBtnDown)}
              onClick={() => setRankFilter("losers")}
            >
              <IconTrendingDown size={14} /> {t("home.HomeMarketRateDown") || "Top Losers"}
            </button>
          </div>
        </div>

        <div className={styles.marketsColHead}>
          <span>{t("home.pcName") || "Name"}</span>
          <span className={styles.alignRight}>{t("home.LastPrice") || "Last Price"}</span>
          <span className={styles.alignRight}>{t("home.24hChange") || "24H Change"}</span>
          <span className={styles.alignRight}>{t("home.pcVolume") || "Volume"}</span>
          <span className={styles.alignRight}>{t("home.pcChart") || "Chart"}</span>
        </div>

        <ul className={styles.marketsList}>
          {rows.map((item) => {
            const { base, quote } = parseSymbolParts(item.s);
            const cfg = market.marketConfig?.[item.s] || market.marketConfig?.[`${item.s}_cfd`];
            const displayBase =
              (fund?.getCurrencyDisplayName && fund.getCurrencyDisplayName(base)) ||
              base.toUpperCase();
            const displayQuote =
              (fund?.getCurrencyDisplayName && fund.getCurrencyDisplayName(quote)) ||
              quote.toUpperCase();
            const name =
              fund?.currencyMap?.[base]?.fullName || fund?.currencyMap?.[base]?.name || base;
            const chartData = getChartData(item.s);
            return (
              <li key={item.s}>
                <a href={getMarketRowHref(item.s, isSpot)} className={styles.marketRow}>
                  <div className={styles.marketNameCell}>
                    <CoinIcon symbol={item.s} fund={fund} />
                    <div>
                      <div className={styles.marketPair}>
                        {displayBase}
                        <span className={styles.marketPairQuote}>/{displayQuote}</span>
                      </div>
                      <div className={styles.marketFullName}>{name}</div>
                    </div>
                  </div>
                  <div className={classNames(styles.marketPrice, styles.alignRight)}>
                    ${formatPrice(item.c, cfg?.buyPoint ?? 2)}
                  </div>
                  <div className={classNames(styles.marketChange, styles.alignRight)}>
                    <ChangePill doc={item} />
                  </div>
                  <div className={classNames(styles.marketVol, styles.alignRight)}>
                    ${fmtCompact(item.v)}
                  </div>
                  <div className={styles.marketChart}>
                    {chartData?.length ? (
                      <div
                        className={styles.sparklineCell}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <LineChart
                          itemKey={`home_pc_spark_${item.s}`}
                          chartData={chartData}
                          theme={chartThemeClass(item)}
                          width={56}
                          height={22}
                        />
                      </div>
                    ) : (
                      <span className={styles.chartPlaceholder}>—</span>
                    )}
                  </div>
                </a>
              </li>
            );
          })}
          {!rows.length ? (
            <li className={styles.moversEmpty}>
              {showLoading
                ? t("home.pcLoadingMarkets") || "Loading…"
                : t("common.noData") || "No data"}
            </li>
          ) : null}
        </ul>

        <div className={styles.marketsFooter}>
          <a href={getUrl("/market")} className={styles.sectionLink}>
            {t("home.pcViewMore") || "View More"} <IconArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

export default observer(MarketsSection);
